import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authSlice } from '../services/stores/auth/slice';
import { AuthAPI } from '../services/apis/AuthAPI';
import { isTokenExpired, isValidTokenFormat } from '../services/apis/configs/apiClient';

/**
 * Custom hook to handle authentication persistence across page refreshes
 */
export const useAuthPersistence = () => {
  const dispatch = useDispatch();
  const authState = useSelector((store: any) => store.auth);
  const hasInitialized = useRef(false);
  const validationInProgress = useRef(false);

  useEffect(() => {
    if (hasInitialized.current || validationInProgress.current) {
      return;
    }

    hasInitialized.current = true;
    
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("token");

      if (!storedToken) {
        dispatch(authSlice.actions.logout(""));
        return;
      }

      if (!isValidTokenFormat(storedToken) || isTokenExpired(storedToken)) {
        dispatch(authSlice.actions.logout(""));
        return;
      }

      if (authState.logged && authState.me?.id && !authState.loading) {
        return;
      }

      if (!authState.loading) {
        console.log('useAuthPersistence: Not in loading state, skipping validation');
        return;
      }

      validationInProgress.current = true;

      try {
        console.log('useAuthPersistence: Validating token with server...');
        const result = await AuthAPI.me(storedToken);

        if (result?.data?.data) {
          console.log('useAuthPersistence: Token valid, setting auth state');
          dispatch(authSlice.actions.setTokenAndMe({
            token: storedToken,
            me: result.data.data
          }));
        } else {
          console.error('useAuthPersistence: Invalid API response structure:', result);
          dispatch(authSlice.actions.logout(""));
        }
      } catch (error: any) {
        console.error('useAuthPersistence: Token validation failed:', error);
        
        const isAuthError = 
          error?.errors?.[0]?.status === 401 ||
          error?.errors?.[0]?.status === 403 ||
          error?.status === 401 ||
          error?.status === 403 ||
          error === 403;

        if (isAuthError) {
          dispatch(authSlice.actions.logout(""));
        } else {
          dispatch(authSlice.actions.setLoading(false));
        }
      } finally {
        validationInProgress.current = false;
      }
    };    
    const timeoutId = setTimeout(initializeAuth, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return authState;
};
